import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, homeFor } from "@/context/AuthContext";
import { api, errMsg, useFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, ErrorBox } from "@/components/Common";
import { Brand } from "@/layouts/CustomerLayout";

const Shell = ({ title, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
    <div className="w-full max-w-md">
      <div className="mb-6 flex justify-center"><Brand sub="Job booking portal" /></div>
      <div className="rounded-lg border border-border bg-card p-6 shadow-sm sm:p-8">
        <h1 className="mb-6 font-heading text-2xl font-semibold" data-testid="auth-title">{title}</h1>
        {children}
      </div>
      <p className="mt-4 text-center text-xs text-muted-foreground"><Link to="/privacy" className="underline">Privacy information</Link></p>
    </div>
  </div>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { const u = await login(f.email, f.password); nav(u.role === "customer" && next?.startsWith("/portal") ? next : homeFor(u)); } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Sign in">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <ErrorBox error={err} />
        <Field label="Email address" id="email"><Input id="email" type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" /></Field>
        <Field label="Password" id="password"><Input id="password" type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" /></Field>
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-btn">{busy ? "Signing in…" : "Sign in"}</Button>
      </form>
      <div className="mt-5 flex justify-between text-sm">
        <Link to="/forgot-password" className="text-primary underline" data-testid="forgot-link">Forgot password?</Link>
        <Link to={`/register${next ? `?next=${next}` : ""}`} className="text-primary underline" data-testid="register-link">Create an account</Link>
      </div>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [errs, setErrs] = useState({});
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const validate = () => {
    const x = {};
    if (f.name.trim().length < 2) x.name = "Enter your full name";
    if (!/^\S+@\S+\.\S+$/.test(f.email)) x.email = "Enter a valid email address";
    if (!/^\+?[\d\s()-]{10,20}$/.test(f.phone.trim())) x.phone = "Enter a valid UK telephone number";
    if (f.password.length < 8 || !/\d/.test(f.password) || !/[a-z]/i.test(f.password)) x.password = "At least 8 characters with letters and numbers";
    if (f.password !== f.confirm) x.confirm = "Passwords do not match";
    setErrs(x);
    return !Object.keys(x).length;
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true); setErr(null);
    try {
      await register({ name: f.name, email: f.email, phone: f.phone, password: f.password });
      toast.success("Account created");
      nav(next?.startsWith("/portal") ? next : "/portal");
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your account">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <ErrorBox error={err} />
        <Field label="Full name" id="name" error={errs.name} required><Input id="name" autoComplete="name" value={f.name} onChange={set("name")} data-testid="register-name-input" /></Field>
        <Field label="Email address" id="remail" error={errs.email} required><Input id="remail" type="email" autoComplete="email" value={f.email} onChange={set("email")} data-testid="register-email-input" /></Field>
        <Field label="Telephone number" id="phone" error={errs.phone} required><Input id="phone" type="tel" autoComplete="tel" value={f.phone} onChange={set("phone")} data-testid="register-phone-input" /></Field>
        <Field label="Password" id="rpw" error={errs.password} hint="At least 8 characters, including letters and numbers" required><Input id="rpw" type="password" autoComplete="new-password" value={f.password} onChange={set("password")} data-testid="register-password-input" /></Field>
        <Field label="Confirm password" id="rpw2" error={errs.confirm} required><Input id="rpw2" type="password" autoComplete="new-password" value={f.confirm} onChange={set("confirm")} data-testid="register-confirm-input" /></Field>
        <p className="text-xs text-muted-foreground">We only use your details to handle your landscaping enquiries. See our <Link to="/privacy" className="underline">privacy information</Link>.</p>
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit-btn">{busy ? "Creating account…" : "Create account"}</Button>
      </form>
      <p className="mt-5 text-sm">Already registered? <Link to={`/login${next ? `?next=${next}` : ""}`} className="text-primary underline" data-testid="to-login-link">Sign in</Link></p>
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { const r = await api.post("/auth/forgot-password", { email }); setMsg(r.data.message); } catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Shell title="Reset your password">
      {msg ? <p className="rounded-md bg-secondary p-3 text-sm" data-testid="forgot-success">{msg}</p> : (
        <form onSubmit={submit} className="space-y-4">
          <ErrorBox error={err} />
          <Field label="Email address" id="femail"><Input id="femail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></Field>
          <Button type="submit" className="w-full" data-testid="forgot-submit-btn">Send reset link</Button>
        </form>
      )}
      <p className="mt-5 text-sm"><Link to="/login" className="text-primary underline">Back to sign in</Link></p>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(null);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password: pw }); toast.success("Password updated"); nav("/login"); }
    catch (e2) { setErr(errMsg(e2)); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        <ErrorBox error={err} />
        <Field label="New password" id="npw" hint="At least 8 characters, including letters and numbers"><Input id="npw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" /></Field>
        <Button type="submit" className="w-full" data-testid="reset-submit-btn">Update password</Button>
      </form>
    </Shell>
  );
}

export function Privacy() {
  const { data } = useFetch("/public/info");
  return (
    <Shell title="Privacy information">
      <p className="whitespace-pre-wrap text-sm" data-testid="privacy-text">{data?.privacy_notice}</p>
      {data && <p className="mt-4 text-sm text-muted-foreground">Retention: job records and photographs are kept for {data.retention_months} months after your last activity, then deleted or anonymised.</p>}
      <p className="mt-5 text-sm"><Link to="/" className="text-primary underline">Back to portal</Link></p>
    </Shell>
  );
}
